import clsx from 'clsx';
import { updateLessonItem } from 'core/crud/contents.crud';
import { useTranslate } from 'core/hooks/useTranslate';
import { parseSessionsText } from 'core/utils/sessions';
import { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';

function useSessionsTag({ canEditSessions, lessonItemGuid, onSaveSessions, sessions }) {
  const { t } = useTranslate();
  const { addToast } = useToasts();
  const [openDialog, setOpenDialog] = useState(false);
  const [parsedSessions, setParsedSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessions?.length > 0) {
      setParsedSessions(parseSessionsText(sessions, t));
    } else {
      setParsedSessions([]);
    }
  }, [sessions]);

  // En edición el clic debe propagarse: el ClickAwayListener del Popover cierra así los menús abiertos. En lectura no, para no abrir la tarjeta.
  function onClickChangeSessions(event) {
    if (canEditSessions) {
      setOpenDialog(true);
    } else {
      event.stopPropagation();
    }
  }

  function onCloseDialog() {
    setOpenDialog(false);
  }
  async function onSuccessDialog(value) {
    setLoading(true);
    try {
      const response = await updateLessonItem({
        guid: lessonItemGuid,
        session_ids: value.map((session) => session.id),
      });
      if (response.status === 200) {
        addToast(t('lesson:Resource updated successfully'), {
          appearance: 'success',
          autoDismiss: true,
        });
        if (onSaveSessions) {
          onSaveSessions({ session: value.map((session) => ({ session_id: session.id, session_order: session.order, session_resource_type: 'session' })) });
        }
      } else {
        addToast(t('lesson:Could not update resource'), {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      onCloseDialog();
    }
  }

  const sessionTexts = parsedSessions.map((session) => session.text);
  const firstSessionText = sessionTexts[0] ?? '';
  const additionalSessionsCount = Math.max(sessionTexts.length - 1, 0);
  const editSessionsLabel = `${firstSessionText}. ${t('common:Click to edit the assigned sessions')}`;
  const additionalSessionsLabel = t('common:Additional sessions info', {
    count: additionalSessionsCount,
    sessions: sessionTexts.slice(1).join(', '),
  });
  const additionalSessionsButtonClassName = clsx('button-accessibility-only', 'sessions-tag__additional-button', {
    'sessions-tag__additional-button--clickable': canEditSessions,
  });

  return {
    additionalSessionsButtonClassName,
    additionalSessionsCount,
    additionalSessionsHasPopup: canEditSessions ? 'dialog' : undefined,
    additionalSessionsLabel,
    additionalSessionsTagLabel: `+${additionalSessionsCount}`,
    allSessionsText: sessionTexts.join(', '),
    editSessionsLabel,
    firstSessionText,
    loading,
    onClickChangeSessions,
    onCloseDialog,
    onSuccessDialog,
    openDialog,
    shouldShowEditableSession: !!firstSessionText && !!canEditSessions,
    shouldShowReadOnlySession: !!firstSessionText && !canEditSessions,
  };
}

export default useSessionsTag;
